<template>
  <div class="customise-route-style">
    <!-- Route type tabs -->
    <div class="route-type-tabs">
      <button
        v-for="tab in routeTabs"
        :key="tab.key"
        class="route-type-tab"
        :class="{ 'route-type-tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key as RouteTab"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Preview -->
    <div class="preview-container mb-4">
      <svg width="100%" height="72" viewBox="0 0 300 72" xmlns="http://www.w3.org/2000/svg">
        <rect width="300" height="72" fill="#1e2530" />
        <line x1="0" y1="24" x2="300" y2="24" stroke="#263040" stroke-width="1"/>
        <line x1="0" y1="48" x2="300" y2="48" stroke="#263040" stroke-width="1"/>
        <line x1="75"  y1="0" x2="75"  y2="72" stroke="#263040" stroke-width="1"/>
        <line x1="150" y1="0" x2="150" y2="72" stroke="#263040" stroke-width="1"/>
        <line x1="225" y1="0" x2="225" y2="72" stroke="#263040" stroke-width="1"/>
        <path
          d="M 15 52 Q 55 18 95 42 Q 135 62 175 28 Q 215 8 260 36 L 285 26"
          :stroke="currentPrefs.color"
          :stroke-width="previewStrokeWidth"
          :stroke-opacity="currentPrefs.opacity / 100"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
    </div>

    <!-- Colour -->
    <div class="style-section">
      <p class="section-label">Route colour</p>
      <div class="color-swatches">
        <button
          v-for="color in colorOptions"
          :key="color"
          class="color-swatch"
          :class="{ 'color-swatch--active': currentPrefs.color === color }"
          :style="{ background: color }"
          @click="setColor(color)"
        />
      </div>
    </div>

    <!-- Thickness -->
    <div class="style-section">
      <p class="section-label">Route thickness</p>
      <div class="thickness-options">
        <button
          v-for="t in thicknessOptions"
          :key="t.value"
          class="thickness-btn"
          :class="{ 'thickness-btn--active': currentPrefs.thickness === t.value }"
          @click="setThickness(t.value)"
        >
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- Opacity -->
    <div class="style-section">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <p class="section-label mb-0">Route opacity</p>
        <span class="opacity-value">{{ currentPrefs.opacity }}%</span>
      </div>
      <input
        type="range"
        class="opacity-slider w-100"
        min="10"
        max="100"
        step="1"
        :value="currentPrefs.opacity"
        @input="setOpacity(+($event.target as HTMLInputElement).value)"
      />
    </div>

    <!-- Reset -->
    <button class="reset-btn mt-3" @click="resetToDefault">Reset to default</button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import emitter from '../../../utils/bus';
import {
  ROUTE_STYLE_STORAGE_KEY,
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  RouteStyleSettings,
  RouteTypePrefs,
} from '../../../utils/routeStyleSettings';

export type RouteTab = keyof Omit<RouteStyleSettings, 'hideMileMarkers'>;

export default defineComponent({
  name: 'CustomiseRouteStyleView',

  data() {
    return {
      activeTab: 'main' as RouteTab,
      settings: getRouteStyleSettings(),
      routeTabs: [
        { key: 'main',      label: 'Main'      },
        { key: 'alt',       label: 'Alt'       },
        { key: 'spur',      label: 'Spur'      },
        { key: 'recording', label: 'Recording' },
      ],
      colorOptions: [
        '#DB2B35', '#F57700', '#FDDF00',
        '#00FFFF', '#00FF8C', '#005BE4',
        '#9E00FF', '#FF00FF', '#FF6F61', '#22242A',
      ],
      thicknessOptions: [
        { label: '0.5×', value: 0.5 },
        { label: '1×',   value: 1   },
        { label: '2×',   value: 2   },
        { label: '3×',   value: 3   },
      ],
      onCloudHydrate: (() => {}) as (() => void),
    };
  },

  computed: {
    currentPrefs(): RouteTypePrefs {
      return this.settings[this.activeTab];
    },
    previewStrokeWidth(): number {
      return Math.max(2, this.currentPrefs.thickness * 5);
    },
  },

  // Refresh local state whenever the cloud-sync bridge hydrates localStorage,
  // so a value pushed from Android/iOS shows up in this dialog without a
  // page reload.
  created() {
    this.onCloudHydrate = () => { this.settings = getRouteStyleSettings(); };
    emitter.on('route-style-settings:changed', this.onCloudHydrate);
  },
  beforeUnmount() {
    if (this.onCloudHydrate) {
      emitter.off('route-style-settings:changed', this.onCloudHydrate);
    }
  },

  methods: {
    setColor(color: string) {
      this.settings[this.activeTab].color = color;
      this.persist();
    },
    setThickness(value: number) {
      this.settings[this.activeTab].thickness = value;
      this.persist();
    },
    setOpacity(value: number) {
      this.settings[this.activeTab].opacity = value;
      this.persist();
    },
    resetToDefault() {
      this.settings = {
        main:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.main      },
        alt:       { ...DEFAULT_ROUTE_STYLE_SETTINGS.alt       },
        spur:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.spur      },
        recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.recording },
        hideMileMarkers: false,
      };
      this.persist();
    },
    persist() {
      try {
        localStorage.setItem(ROUTE_STYLE_STORAGE_KEY, JSON.stringify(this.settings));
      } catch {}
      emitter.emit('route-style-settings:changed', { ...this.settings });
    },
  },
});
</script>

<style scoped>
.customise-route-style {
  padding: 16px;
  padding-bottom: 24px;
}

.route-type-tabs {
  display: flex;
  background: var(--gray);
  border-radius: 9999px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 20px;
}

.route-type-tab {
  flex: 1;
  border: none;
  background: transparent;
  color: #aaa;
  border-radius: 9999px;
  padding: 7px 0;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.route-type-tab--active {
  background: #fddf00;
  color: #17181d;
}

.preview-container {
  border-radius: 12px;
  overflow: hidden;
}

.section-label {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
}

.style-section {
  margin-bottom: 24px;
}

.color-swatches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.color-swatch {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition: box-shadow 0.15s;
}

.color-swatch--active {
  box-shadow: 0 0 0 2px #17181d, 0 0 0 4px #fddf00;
}

.thickness-options {
  display: flex;
  gap: 8px;
}

.thickness-btn {
  flex: 1;
  padding: 8px 0;
  border: 1.5px solid #444;
  border-radius: 9999px;
  background: transparent;
  color: #aaa;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.thickness-btn--active {
  border-color: #fddf00;
  color: #fddf00;
}

.opacity-value {
  color: #fddf00;
  font-weight: 700;
  font-size: 0.9rem;
}

.opacity-slider {
  accent-color: #fddf00;
  cursor: pointer;
}

.reset-btn {
  width: 100%;
  padding: 12px;
  background: var(--gray);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #3a3a3a;
}
</style>
